import { NgTemplateOutlet } from "@angular/common";
import {
    Component,
    computed,
    numberAttribute,
    resource,
    signal,
    input,
    ViewEncapsulation,
    ChangeDetectionStrategy,
} from "@angular/core";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { S25EditableTextComponent } from "../../modules/s25-editable/s25-editable-text/s25.editable.text.component";
import { S25IconComponent } from "../../modules/s25-icon/s25.icon.component";
import { S25Icons } from "../../modules/s25-icon/s25.icon.const";
import { Item } from "../../pojo/Item";
import { NameService } from "../../services/name.service";
import { ObjectPermissionService } from "../../services/object.permission.service";
import { OrganizationService } from "../../services/organization.service";
import { PreferenceService } from "../../services/preference.service";
import { ResourceService } from "../../services/resource.service";
import { SpaceService } from "../../services/space.service";
import { UserprefService } from "../../services/userpref.service";
import { S25Const } from "../../util/s25-const";
import { S25Util } from "../../util/s25-util";
import { S25Eval25ScoreComponent } from "../s25-eval25-score/s25.eval25.score.component";
import { S25FavoriteButtonComponent } from "../s25-favorite-button/s25.favorite.button.component";

const EditableItemTypes: Item.Ids[] = [Item.Ids.Organization, Item.Ids.Location, Item.Ids.Resource];
const FormalNameItemTypes: Item.Ids[] = [Item.Ids.Organization, Item.Ids.Location];

@TypeManagerDecorator("s25-ng-item-name")
@Component({
    selector: "s25-ng-item-name",
    template: `
        @if (itemTypeIcon()) {
            <s25-ng-icon
                class="ng-item-name__type-icon"
                [type]="itemTypeIcon()!"
                [autoDarkMode]="true"
                svgClass="c-svgIcon {{ isHeader() ? 'page-header' : '' }}"
                [ariaLabel]="itemTypeName()"
            />
        }
        <div class="ng-item-name__core-info">
            <div class="ng-item-name__core-header">
                @if (canEdit() && nameRq.hasValue()) {
                    <s25-ng-editable-text
                        #nameComp
                        class="ng-item-name__name"
                        fieldId="item-name"
                        [class.header]="isHeader()"
                        [val]="name()"
                        [allowEmpty]="false"
                        [readonlyTemplate]="readonlyName"
                        [disabled]="pendingName() !== undefined"
                        [hasCommit]="true"
                        [hasCancelButton]="true"
                        [hasCommitButton]="true"
                        [onCommit]="updateNameFn"
                        [max]="40"
                    ></s25-ng-editable-text>
                } @else {
                    <div class="ng-item-name__name--readonly">
                        <ng-container *ngTemplateOutlet="readonlyName; context: { $implicit: name() }"></ng-container>
                    </div>
                }
                <ng-template #readonlyName let-name>
                    <h1 class="ng-item-name__name-heading">{{ name }}</h1>
                </ng-template>
                <s25-ng-favorite-button
                    class="ng-item-name__favorite-button"
                    [itemId]="itemId()"
                    [itemTypeId]="itemTypeId()"
                ></s25-ng-favorite-button>
            </div>
            @if (hasFormalName() && !this.formalNameRq.error()) {
                @if (canEdit() && formalNameRq.hasValue()) {
                    <s25-ng-editable-text
                        class="ng-item-name__formal-name"
                        fieldId="item-formal-name"
                        [class.header]="isHeader()"
                        [val]="formalName()"
                        [readonlyTemplate]="readonlyFormalName"
                        [disabled]="pendingFormalName() !== undefined"
                        [hasCommit]="true"
                        [hasCancelButton]="true"
                        [hasCommitButton]="true"
                        [onCommit]="updateFormalNameFn"
                        [weakEditButton]="true"
                        [max]="80"
                    ></s25-ng-editable-text>
                    <ng-template #readonlyFormalName let-formalName>
                        @if (formalName) {
                            {{ formalName }}
                        } @else {
                            <span class="ng-item-name__formal-name-placeholder">+ Add Formal Name</span>
                        }
                    </ng-template>
                } @else if (formalName()) {
                    <div class="ng-item-name__formal-name">
                        {{ formalName() }}
                    </div>
                }
            }
        </div>
        @if (eval25Score()) {
            <s25-ng-eval25-score [score]="eval25Score()"></s25-ng-eval25-score>
        }
    `,
    standalone: true,
    host: {
        "[class.ng-item-name]": "true",
    },
    encapsulation: ViewEncapsulation.Emulated,
    styleUrls: ["./s25.item.name.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        S25Eval25ScoreComponent,
        S25IconComponent,
        S25EditableTextComponent,
        S25FavoriteButtonComponent,
        NgTemplateOutlet,
    ],
})
export class S25ItemNameComponent {
    public readonly itemTypeId = input.required({
        transform: (value: string | number): Item.Ids => {
            const asNumber = S25Util.parseInt(value);
            if (asNumber && Item.Ids[asNumber]) {
                return asNumber;
            } else {
                throw new Error(`Invalid itemTypeId: ${value}`);
            }
        },
    });
    public readonly itemId = input.required({ transform: numberAttribute });
    public readonly isHeader = input<boolean>(false);

    // TODO: s25resource would clean all of these resources up
    public readonly nameRq = resource({
        params: () => ({ id: this.itemId(), typeId: this.itemTypeId() }),
        loader: ({ params }) => NameService.getName(params.id, params.typeId),
    });
    public readonly pendingName = signal<string | undefined>(undefined);
    public readonly name = computed(() => {
        if (this.pendingName() !== undefined) {
            return this.pendingName();
        } else if (this.nameRq.error()) {
            if ((<any>this.nameRq.error()).cause?.data?.results?.error?.msg_id === "RM_I_NOPERM") {
                return S25Const.private;
            }
        } else if (this.nameRq.isLoading()) {
            return "Loading...";
        } else if (this.nameRq.hasValue()) {
            return this.nameRq.value();
        }
        return "Error Fetching Name";
    });
    public readonly hasFormalName = computed(() => FormalNameItemTypes.includes(this.itemTypeId()));
    public readonly formalNameRq = resource({
        params: () => {
            if (this.hasFormalName()) {
                return { id: this.itemId(), typeId: this.itemTypeId() };
            }
        },
        loader: async ({ params }) =>
            (await NameService.getFormal(params.id, params.typeId))
                // handle the fact that organization can return undefined formal name
                ?.trim() ?? "",
    });
    public readonly pendingFormalName = signal<string | undefined>(undefined);
    public readonly formalName = computed(() => {
        if (this.pendingFormalName() !== undefined) {
            return this.pendingFormalName();
        } else if (this.formalNameRq.hasValue()) {
            return this.formalNameRq.value();
        }
    });
    private readonly canEditRq = resource({
        params: () => {
            if (EditableItemTypes.includes(this.itemTypeId())) {
                return { id: this.itemId(), typeId: this.itemTypeId() };
            }
        },
        loader: ({ params }) => ObjectPermissionService.canEdit(params.id, params.typeId),
        defaultValue: false,
    });
    public readonly canEdit = computed(() => !!(this.canEditRq.hasValue() && this.canEditRq.value()));
    private readonly eval25EnabledRq = resource({
        params: () => (this.itemTypeId() === Item.Ids.Location ? { prefs: ["Eval25"] } : undefined),
        loader: ({ params }) => PreferenceService.getPreferences(params.prefs),
        defaultValue: { Eval25: { type: "B", value: "false" } },
    });
    private readonly eval25ScoresRq = resource({
        params: () => {
            if (this.eval25EnabledRq.hasValue() && this.eval25EnabledRq.value().Eval25.value === "true") {
                return this.itemId();
            }
        },
        loader: ({ params }) => SpaceService.getSpaceSatisfaction(params),
    });
    public readonly eval25Score = computed(() => {
        if (this.eval25ScoresRq.hasValue()) {
            const scores = this.eval25ScoresRq.value();
            const eval25Data = scores?.length && scores[scores.length - 1]?.rating;

            if (eval25Data) {
                return Math.round(eval25Data * 10) / 10;
            }
        }
    });
    public readonly isLoggedInRq = resource({
        loader: () => UserprefService.getLoggedIn(),
    });
    public readonly isLoggedIn = computed(() => this.isLoggedInRq.hasValue() && this.isLoggedInRq.value());
    public readonly itemTypeIcon = computed(() => Item.TypeIcons[this.itemTypeId()]);
    public readonly itemTypeName = computed((): string | undefined => Item.Ids[this.itemTypeId()]);

    // lambda to work around [onCommit] being an input instead of output
    public updateNameFn = (newName: string) => this.updateName(newName);
    public updateName(newName: string) {
        newName = newName.trim();
        if (!this.canEdit() || newName === this.name()) {
            return;
        }
        const updateRq = this.requestUpdateName(newName);
        if (!updateRq) {
            return;
        }
        this.pendingName.set(newName);
        updateRq
            .then(() => this.nameRq.value.set(newName))
            .catch((e) =>
                this.nameRq.value.set(
                    S25Util.errorText(
                        e,
                        "There was an error while updating the object name. Please contact your administrator.",
                    ),
                ),
            )
            .finally(() => this.pendingName.set(undefined));
    }

    // lambda to work around [onCommit] being an input instead of output
    public updateFormalNameFn = (newName: string) => this.updateFormalName(newName);
    public updateFormalName(newName: string = "") {
        newName = newName.trim();
        if (!this.canEdit() || newName === this.formalName()) {
            return;
        }
        const updateRq = this.requestUpdateFormalName(newName);
        if (!updateRq) {
            return;
        }
        this.pendingFormalName.set(newName);
        updateRq
            .then(() => this.formalNameRq.value.set(newName))
            .catch((e) =>
                this.formalNameRq.value.set(
                    S25Util.errorText(
                        e,
                        "There was an error while updating the object name. Please contact your administrator.",
                    ),
                ),
            )
            .finally(() => this.pendingFormalName.set(undefined));
    }

    private requestUpdateName(newName: string): Promise<void> | undefined {
        switch (this.itemTypeId()) {
            case Item.Ids.Organization:
                return OrganizationService.putOrganization(this.itemId(), {
                    organization_name: newName,
                });
            case Item.Ids.Location:
                return SpaceService.microUpdate({
                    id: this.itemId(),
                    spaceName: newName,
                });
            case Item.Ids.Resource:
                return ResourceService.putResource(this.itemId(), { resource_name: newName });
        }
    }

    private requestUpdateFormalName(newName: string): Promise<void> | undefined {
        switch (this.itemTypeId()) {
            case Item.Ids.Organization:
                return OrganizationService.putOrganization(this.itemId(), {
                    organization_title: newName,
                });
            case Item.Ids.Location:
                return SpaceService.microUpdate({
                    id: this.itemId(),
                    spaceFormalName: newName,
                });
        }
    }
}
